import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { useEffect } from 'haunted'
import { explicitBooleanAttributeConverter } from '../directives/explicitBooleanAttributeConverter'
import type { Dialog } from '@fluentui/web-components/dist/dts/dialog/index'
import { getLongestTransitionMS } from '../functions/longestCSSTransition';
import { LitHauntedElement } from '../mixins/lit-haunted-element';

/**
 * Description:
 * Customized <fluent-dialog> with a frame header and (X) to close button
 * 
 * Slots:
 * - "frame-title"
 * - ""
 * Requires that the following be included with the element at runtime:
 * - https://cdn.jsdelivr.net/npm/@fluentui/web-components@2.5.8/dist/web-components.min.js
 */
@customElement('ep-fluent-dialog')
export class EPFluentDialog extends LitHauntedElement {

  /**
   * Pass-thru properties from:
   * - https://github.com/microsoft/fast/blob/master/packages/web-components/fast-foundation/src/dialog/dialog.ts
   */

  @property({ type: Boolean, reflect: true })
  public modal: boolean = false;

  @state()
  private _open: boolean = false;

  @property({ type: Boolean, converter: explicitBooleanAttributeConverter, attribute: 'open', reflect: true })
  public isOpen: boolean = false;

  get hidden(): boolean {
    return !this.isOpen;
  }
  set hidden(value: boolean) {
    this.isOpen = !value;
  }

  @property({ type: Boolean, reflect: true, attribute: 'hide-close-button' })
  public hideCloseButton: boolean = false;

  @property({ type: Boolean, reflect: true, attribute: 'deny-dismissing' })
  public denyDismissing: boolean = false;

  @property({ type: Boolean, reflect: true, attribute: 'trap-focus' })
  public trapFocus: boolean = false;

  //@property({ type: Boolean, attribute: 'show-actions', reflect: true })
  @property({ attribute: false })
  public showActions: boolean = false;

  @property({ type: String, reflect: true })
  public width: string = '640px';

  @property({ type: String, reflect: true })
  public height: string = '480px';

  @query('slot[name=frame-actions]')
  frameActionsSlotElement?: HTMLSlotElement;

  @query('fluent-dialog')
  fluentDialog?: Dialog;

  /** Controls the fade in/out animation */
  @state()
  isHiding: boolean = this.hidden;

  get fluentInnerDialog(): HTMLElement | undefined {
    return this.fluentDialog?.shadowRoot?.querySelector(`[part=control]`) ?? undefined;
  }

  render() {
    useEffect(() => {
      //console.log('effect?', this.isOpen);
      // Dialog is meant to be open
      if (this.isOpen) {
        this.isHiding = true;
        //this.hidden = false;
        this._open = true;
        setTimeout(() => {
          this.isHiding = false;
        }, 0);
      }
      // Dialog is meant to be closed
      else if (this.fluentDialog) {
        const animatedParts = Array.from(this.fluentDialog.shadowRoot?.querySelectorAll(`[part=overlay], [part=control]`) ?? []) as HTMLElement[];
        const delay = Math.max(...animatedParts.map(e => getLongestTransitionMS(e), 0));
        this.isHiding = true;
        setTimeout(() => {
          //this.hidden = true;
          this._open = false;
          this._dispatchDismiss(this);
        }, delay);
      }
    }, [this.isOpen]);

    return html`
      <fluent-dialog
          data-hidden=${this.isHiding}
          .hidden=${!this._open}
          .trapFocus=${this.trapFocus}
          .modal=${this.modal}
          @dismiss=${this._handleDismiss}
          @close=${this._handleDismiss}
          style=${`--dialog-width: ${this.width}; --dialog-height: ${this.height};`}>
        <div class="safety-zone">
          <div class="frame-top" role="toolbar" part="frame-top">
            <div class="frame-top-title">
              <slot name="frame-title">
                <h2>Modal Title</h2>
              </slot>
            </div>
            ${
              this.hideCloseButton
              ? ''
              : html`
              <fluent-button style="align-self: flex-start;" appearance="stealth" @click=${this._handleCloseClick}>Close &#10006;</fluent-button>
              `
            }
          </div>
          <fluent-divider></fluent-divider>
          <div class="modal-content">
            <slot></slot>
          </div>
          ${
            this.showActions
            ? html`
              <fluent-divider></fluent-divider>
              <div class="frame-bottom" role="menubar" part="frame-bottom">
                <slot name="frame-actions" @slotchange=${this._handleFrameActionsSlotChange}>
                  <!-- <fluent-button appearance="accent">Save</fluent-button>
                  <fluent-button appearance="neutral">Cancel</fluent-button> -->
                </slot>
              </div>
            `
            : html`
            <div style="display: none;">
              <slot name="frame-actions" @slotchange=${this._handleFrameActionsSlotChange}></slot>
            </div>
            `
          }
        </div>
      </fluent-dialog>
    `
  }

  public open() {
    // this.isHiding = true;
    // this.hidden = false;
    // setTimeout(() => {
    //   this.isHiding = false;
    // }, 10);
    this.isOpen = true;
  }

  public close() {
    // this.isHiding = true;
    // setTimeout(() => {
    //   this.hidden = true;
    // }, 1000);
    // if (this.fluentDialog && this.isOpen) {
    //   this._dispatchDismiss(this.fluentDialog, true);
    // }
    this.isOpen = false;
  }

  private _handleDismiss(event: CustomEvent) {
    //console.log('dismiss event?', event);
    if (this.fluentDialog) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.denyDismissing || event.detail === true) {
        // const animatedParts = Array.from(this.fluentDialog.shadowRoot?.querySelectorAll(`[part=overlay], [part=control]`) ?? []) as HTMLElement[];
        // const delay = Math.max(...animatedParts.map(e => getLongestTransitionMS(e), 0));
        // this.isHiding = true;
        // setTimeout(() => {
        //   //this.hidden = true;
        //   this._open = false;
        //   this._dispatchDismiss(this);
        // }, delay);
        this.isOpen = false;
      }
      // If the dialog wasn't supposed to close (previous `if`) AND we're supposed to deny dismisses, play a little animation
      else if (this.denyDismissing && this.fluentInnerDialog) {
        //console.log('dismiss denied!')
        // Borrowed from: https://github.com/shoelace-style/shoelace/blob/7500cabc587abead71f2440ad8f87aa4c1f468f9/src/components/dialog/dialog.component.ts#L332-L335
        const keyframes: Keyframe[] = [{ scale: 1 }, { scale: 1.02 }, { scale: 1 }];
        const options: KeyframeAnimationOptions = { duration: 250 };
        this.fluentInnerDialog.getAnimations().forEach(anim => anim.cancel())
        this.fluentInnerDialog.animate(keyframes, options);
      }
    }
  }

  private _handleCloseClick() {
    if (this.fluentDialog) {
      this._dispatchDismiss(this.fluentDialog);
    }
    //this.hidden = true;
  }

  private _dispatchDismiss(target: HTMLElement, bypass: boolean = false) {
    const event = new CustomEvent<boolean | undefined>('dismiss', { 
      bubbles: true,
      composed: true,
      detail: bypass,
    })
    target.dispatchEvent(event);
    //this.dispatchEvent(event);
    //this.fluentDialog?.dispatchEvent(event);
  }

  private _handleFrameActionsSlotChange() {
    this.showActions = (this.frameActionsSlotElement?.assignedElements()?.length ?? 0) > 0;
  }

  static styles = css`
    :host {
      position: relative;
      z-index: 100;
      --color-text-muted: hsla(0, 0%, 15%, 0.7);
      --transition-time-first: 0.3s 0s;
      --transition-time-second: 0.25s 0.16s;
    }

    .safety-zone {
      display: flex;
      flex-direction: column;
      height: 100%;
      border-radius: 8px;
      box-sizing: border-box;
    }

    .frame-top, .frame-bottom {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0.25rem;
      margin: 0;
    }

    .frame-top .frame-top-title {
      flex-grow: 1;
      padding-left: 1rem;
    }

    .frame-bottom {
      justify-content: flex-end;
      gap: 5px;
      padding: 10px;
    }

    .modal-content {
      height: 100%;
      overflow: auto;
    }

    fluent-dialog[data-hidden=true]::part(overlay) {
      opacity: 0.0;
      transition: opacity var(--transition-time-second);
    }

    fluent-dialog[data-hidden=false]::part(overlay) {
      opacity: 1.0;
      transition: opacity var(--transition-time-first);
    }

    fluent-dialog[data-hidden=true]::part(control) {
      opacity: 0.0;
      transform: translateY(20px);
      transition: opacity var(--transition-time-first), transform var(--transition-time-first);
    }

    fluent-dialog[data-hidden=false]::part(control) {
      opacity: 1.0;
      /* transform: translateY(0); */
      transition: opacity var(--transition-time-second), transform var(--transition-time-second);
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-fluent-dialog': EPFluentDialog
  }
}
